// src/admin/NotificationManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Trash2, X, Upload, Image as ImageIcon, Save, Bell,
  Send, Users, CheckCircle, AlertCircle, Mail, Phone, MapPin,
  Search, CheckSquare, Square
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const NotificationManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    buttonText: '',
    buttonLink: '',
  });

  // Fetch customers + notifications
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch customers (exclude admin roles)
      const usersSnap = await getDocs(collection(db, 'users'));
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const adminRoles = ['superadmin', 'admin', 'manager', 'employee'];
      const customersList = allUsers.filter(u => !u.role || u.role === 'customer' || !adminRoles.includes(u.role));
      setCustomers(customersList);

      // Fetch notifications
      const notifSnap = await getDocs(collection(db, 'notifications'));
      const notifData = notifSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      notifData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setNotifications(notifData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const url = await uploadToImageKit(file, 'notifications');
      setFormData({ ...formData, image: url });
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Image upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const toggleCustomer = (id) => {
    setSelectedCustomers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.title || !formData.description) {
      setMessage({ type: 'error', text: 'Title aur Description zaroori hain.' });
      return;
    }

    if (selectedCustomers.length === 0) {
      setMessage({ type: 'error', text: 'Kam az kam ek customer select karein.' });
      return;
    }

    setSending(true);

    try {
      const newId = Date.now().toString();
      await setDoc(doc(db, 'notifications', newId), {
        title: formData.title,
        description: formData.description,
        image: formData.image || '',
        buttonText: formData.buttonText || '',
        buttonLink: formData.buttonLink || '',
        recipients: selectedCustomers,
        createdAt: new Date(),
      });

      setSuccessMessage(`Notification sent to ${selectedCustomers.length} customer(s)!`);
      setShowSuccess(true);

      // Reset form
      setFormData({ title: '', description: '', image: '', buttonText: '', buttonLink: '' });
      setSelectedCustomers([]);
      setSelectAll(false);
      fetchData();
    } catch (error) {
      console.error('Send error:', error);
      setMessage({ type: 'error', text: 'Failed to send notification.' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (notif) => {
    if (!window.confirm(`Delete notification "${notif.title}"?\n\nYe sab customers ke inbox se bhi hat jayega.`)) return;
    try {
      await deleteDoc(doc(db, 'notifications', notif.id));
      setSuccessMessage('Notification deleted from all inboxes!');
      setShowSuccess(true);
      fetchData();
    } catch (error) {
      alert('Failed to delete.');
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-PK', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return '-'; }
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Bell className="text-green-700" size={26} /> Notifications Management
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Customers ko notifications bhejein. Popup + inbox mein show hoga.
        </p>
      </div>

      {message.text && (
        <div className={`text-sm p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'error' && <AlertCircle size={16} />}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* ================= LEFT: Customers ================= */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-4">

            {/* Header */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-gray-800 flex items-center gap-2">
                  <Users size={18} className="text-green-700" />
                  Customers ({customers.length})
                </h2>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700">
                  {selectedCustomers.length} selected
                </span>
              </div>

              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg py-2 pl-9 pr-3 text-sm focus:outline-none focus:border-green-600"
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
              </div>
            </div>

            {/* Select All */}
            <div className="p-3 border-b bg-green-50">
              <button
                onClick={toggleSelectAll}
                className="w-full flex items-center gap-2 text-sm font-semibold text-green-800"
              >
                {selectAll ? <CheckSquare size={18} /> : <Square size={18} />}
                Select All ({customers.length})
              </button>
            </div>

            {/* Customers List */}
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">No customers found.</div>
              ) : (
                filteredCustomers.map(customer => {
                  const selected = selectedCustomers.includes(customer.id);
                  return (
                    <button
                      key={customer.id}
                      onClick={() => toggleCustomer(customer.id)}
                      className={`w-full flex items-start gap-3 p-3 text-left border-b border-gray-100 transition ${
                        selected ? 'bg-green-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="mt-0.5">
                        {selected ? (
                          <CheckSquare size={18} className="text-green-700" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </div>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                        style={{ backgroundColor: '#16a34a' }}>
                        {customer.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800 truncate">
                          {customer.name || 'Unnamed'}
                        </p>
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                          <Mail size={10} /> {customer.email || '-'}
                        </p>
                        {customer.phone && (
                          <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                            <Phone size={10} /> {customer.phone}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ================= RIGHT: Form ================= */}
        <div className="lg:col-span-3 space-y-6">

          {/* Create Notification */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="font-bold text-gray-800 mb-5 flex items-center gap-2">
              <Send size={18} className="text-green-700" /> Create Notification
            </h2>

            <form onSubmit={handleSend} className="space-y-4">

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Mega Sale Live!"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Notification ka message..."
                  required
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Image (Optional)</label>
                {formData.image ? (
                  <div className="relative">
                    <img src={formData.image} alt="preview" className="w-full h-40 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                        <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <p className="text-sm font-semibold text-gray-600">Click to upload image</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>

              {/* Button Text + Link */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Button Text (Optional)</label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Button Link (Optional)</label>
                  <input
                    type="text"
                    value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="e.g., /shop ya /product/xxx"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  Notification <strong>{selectedCustomers.length}</strong> customers ko bhejegi.<br />
                  Popup 5 seconds tak show hoga + inbox mein milega.
                </div>
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={sending || selectedCustomers.length === 0}
                className={`w-full rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ripple-btn ${
                  sending || selectedCustomers.length === 0 ? 'opacity-60 cursor-not-allowed' : ''
                }`}
                style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
              >
                {sending ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                      style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} /> Send to {selectedCustomers.length} Customer(s)
                  </>
                )}
              </button>

            </form>
          </div>

          {/* Sent Notifications History */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Bell size={18} className="text-green-700" /> Sent Notifications ({notifications.length})
            </h2>

            {notifications.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No notifications sent yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notifications.map(notif => (
                  <div key={notif.id} className="p-3 rounded-lg border border-gray-200 hover:border-green-300 transition">
                    <div className="flex items-start gap-3">
                      {notif.image && (
                        <img src={notif.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800 truncate">{notif.title}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">{notif.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                          <span>📅 {formatDate(notif.createdAt)}</span>
                          <span className="flex items-center gap-1">
                            <Users size={10} /> {notif.recipients?.length || 0} recipients
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(notif)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition shrink-0"
                        title="Delete from all inboxes"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default NotificationManagement;